'use client'

import { Search, XIcon } from 'lucide-react'
import { forwardRef, type FocusEventHandler } from 'react'

import { cn } from '@/_lib/utils'

export type ZSearchFieldProps = {
	value: string
	onValueChange: (value: string) => void
	placeholder?: string
	ariaLabel?: string
	className?: string
	inputClassName?: string
	onFocus?: FocusEventHandler<HTMLInputElement>
	onBlur?: FocusEventHandler<HTMLInputElement>
}

/**
 * Controlled search input — layout-neutral (full width of parent); reuse anywhere.
 */
export const ZSearchField = forwardRef<HTMLInputElement, ZSearchFieldProps>(function ZSearchField(
	{
		value,
		onValueChange,
		placeholder = 'Hľadať…',
		ariaLabel,
		className,
		inputClassName,
		onFocus,
		onBlur
	},
	ref
) {
	const hasValue = value.length > 0

	return (
		<div
			className={cn(
				'group relative flex w-full items-center rounded-full border border-solid bg-transparent',
				/* Neaktívny ako pill: jemný border; aktívny (hodnota alebo focus) ako vybraný pill. */
				'border-medante-purple-dark/30 transition-[border-color,box-shadow] duration-300',
				'hover:border-medante-purple-dark focus-within:border-medante-purple-dark',
				hasValue && 'border-medante-purple-dark',
				className
			)}
		>
			<Search
				className={cn(
					'ml-4 size-4 shrink-0 transition-colors duration-300 md:ml-6',
					hasValue
						? 'text-medante-purple-dark'
						: 'text-medante-purple-dark/40 group-hover:text-medante-purple-dark/60 group-focus-within:text-medante-purple-dark'
				)}
				aria-hidden
				strokeWidth={2}
			/>
			<input
				ref={ref}
				type='text'
				inputMode='search'
				enterKeyHint='search'
				role='searchbox'
				autoComplete='off'
				value={value}
				onChange={e => onValueChange(e.target.value)}
				onFocus={onFocus}
				onBlur={onBlur}
				placeholder={placeholder}
				aria-label={ariaLabel ?? placeholder}
				className={cn(
					'min-w-0 flex-1 appearance-none rounded-full border-0 bg-transparent py-2.5 pl-2 text-sm md:py-3',
					hasValue ? 'pr-11' : 'pr-4 md:pr-6',
					'text-medante-purple-dark transition-[color] duration-300',
					'placeholder:text-medante-purple-dark/40 placeholder:transition-colors placeholder:duration-300',
					'group-hover:placeholder:text-medante-purple-dark/60',
					'group-focus-within:placeholder:text-medante-purple-dark',
					'outline-none focus:bg-transparent focus:outline-none focus:ring-0',
					inputClassName
				)}
			/>
			<button
				type='button'
				tabIndex={hasValue ? 0 : -1}
				disabled={!hasValue}
				onMouseDown={e => e.preventDefault()}
				onClick={() => onValueChange('')}
				aria-label='Vymazať vyhľadávanie'
				className={cn(
					'absolute right-2.5 top-1/2 -translate-y-1/2 cursor-pointer rounded-full p-1',
					'text-medante-purple-dark transition-[color,opacity] duration-300',
					'bg-transparent hover:bg-medante-purple-dark hover:text-white',
					'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-medante-purple-dark/40',
					hasValue ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
				)}
			>
				<XIcon className='size-4 shrink-0' aria-hidden strokeWidth={2} />
			</button>
		</div>
	)
})
